import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import customerService from '../services/customerService';
import Input from '../components/Input';
import Button from '../components/Button';
import './TechnicianForm.css';

const STATE_CITY_MAP = {
  Maharashtra: ['Mumbai', 'Pune', 'Nagpur', 'Nashik', 'Thane', 'Aurangabad'],
  Delhi: ['New Delhi', 'Delhi'],
  Karnataka: ['Bengaluru', 'Mysuru', 'Mangaluru', 'Hubballi'],
  Gujarat: ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Noida', 'Ghaziabad', 'Varanasi'],
  Rajasthan: ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota'],
  'West Bengal': ['Kolkata', 'Howrah', 'Durgapur'],
  Telangana: ['Hyderabad', 'Warangal'],
  'Madhya Pradesh': ['Indore', 'Bhopal', 'Gwalior', 'Jabalpur'],
  Bihar: ['Patna', 'Gaya'],
  Punjab: ['Ludhiana', 'Amritsar', 'Jalandhar'],
  Haryana: ['Gurugram', 'Faridabad', 'Panipat'],
  Kerala: ['Kochi', 'Thiruvananthapuram', 'Kozhikode'],
  Odisha: ['Bhubaneswar', 'Cuttack'],
  Assam: ['Guwahati'],
  Goa: ['Panaji', 'Margao'],
};

const CustomerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    alternatePhone: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    customerType: 'residential',
    gstNumber: '',
    tags: '',
    notes: '',
  });

  const stateOptions = useMemo(
    () => Object.keys(STATE_CITY_MAP).sort(),
    []
  );

  const cityOptions = useMemo(() => {
    if (!formData.state || !STATE_CITY_MAP[formData.state]) return [];
    return STATE_CITY_MAP[formData.state];
  }, [formData.state]);

  useEffect(() => {
    if (isEdit) {
      fetchCustomer();
    }
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const response = await customerService.getById(id);
      const customer = response.data;
      setFormData({
        name: customer.name || '',
        phone: customer.phone || '',
        email: customer.email || '',
        alternatePhone: customer.alternatePhone || '',
        street: customer.address?.street || '',
        city: customer.address?.city || '',
        state: customer.address?.state || '',
        zipCode: customer.address?.zipCode || '',
        customerType: customer.customerType || 'residential',
        gstNumber: customer.gstNumber || '',
        tags: (customer.tags || []).join(', '),
        notes: customer.notes || '',
      });
    } catch (error) {
      toast.error('Failed to fetch customer details');
      navigate('/customers');
    }
  };

  const handleChange = (e) => {
    if (e.target.name === 'state') {
      setFormData({
        ...formData,
        state: e.target.value,
        city: '',
      });
      return;
    }
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const tagsArray = formData.tags
      ? formData.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
      : [];

    const submitData = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email || undefined,
      alternatePhone: formData.alternatePhone || undefined,
      address: {
        street: formData.street || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zipCode: formData.zipCode || undefined,
      },
      customerType: formData.customerType,
      gstNumber: formData.gstNumber || undefined,
      notes: formData.notes || undefined,
      tags: tagsArray,
    };

    try {
      if (isEdit) {
        await customerService.update(id, submitData);
        toast.success('Customer updated successfully');
      } else {
        await customerService.create(submitData);
        toast.success('Customer added successfully');
      }
      navigate('/customers');
    } catch (error) {
      const message =
        error.response?.data?.message || 'Failed to save customer';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <button onClick={() => navigate('/customers')} className="back-button">
          <FiArrowLeft /> Back
        </button>
        <h2 className="form-title">
          {isEdit ? 'Edit Customer' : 'Add New Customer'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3 className="section-title">Customer Details</h3>
          <div className="form-grid">
            <Input
              label="Full Name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Rajesh Kumar"
              required
            />

            <Input
              label="Primary Phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="9876543210"
              required
            />

            <Input
              label="Email Address"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="customer@example.com"
            />

            <Input
              label="Alternate Phone"
              type="tel"
              name="alternatePhone"
              value={formData.alternatePhone}
              onChange={handleChange}
              placeholder="9123456780"
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Address</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">State</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">Select State</option>
                {stateOptions.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label className="input-label">City</label>
              <select
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="input-field"
                disabled={!formData.state || cityOptions.length === 0}
              >
                <option value="">
                  {formData.state ? 'Select City' : 'Select state first'}
                </option>
                {cityOptions.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Street Address"
              type="text"
              name="street"
              value={formData.street}
              onChange={handleChange}
              placeholder="Building A, Floor 5"
            />

            <Input
              label="Zip Code"
              type="text"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              placeholder="400001"
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Additional Information</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">Customer Type</label>
              <select
                name="customerType"
                value={formData.customerType}
                onChange={handleChange}
                className="input-field"
              >
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
                <option value="industrial">Industrial</option>
                <option value="other">Other</option>
              </select>
            </div>

            <Input
              label="GST Number"
              type="text"
              name="gstNumber"
              value={formData.gstNumber}
              onChange={handleChange}
              placeholder="27AABCU9603R1ZM"
            />

            <Input
              label="Tags (comma separated)"
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              placeholder="AMC, Priority, VIP"
            />
          </div>

          <div className="input-group">
            <label className="input-label">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Any special instructions or notes..."
              className="input-field"
              rows="3"
            />
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/customers')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon={<FiSave />}
            loading={loading}
          >
            {isEdit ? 'Update Customer' : 'Add Customer'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CustomerForm;

